import React, {useEffect, useRef, useState} from "react";
import {
    Button,
    Checkbox,
    Col,
    DatePicker,
    Form,
    InputNumber,
    Modal,
    Row,
    Select,
    Spin,
    Tabs,
    Tooltip,
    message,
    Table
} from "antd";
import api from "../../api";
import dayjs from "dayjs";
import CropInfo from "../../Components/CropInfo";
import {useParams, useNavigate} from "react-router-dom";
import PlantingUpdates from "../../Components/PlantingUpdates";
import PlantingPreview from "../../Components/PlantingPreview";
import {PLANTING_STATUS, PLANTING_STATUS_LABELS} from "../../constants";
import {
    calculateCropDates,
    calculateCropDatesFromHarvest, calculateCropDatesFromPlanting,
    calculateRowsAndPlants, printPrice, safeFormatDate
} from "../../helpers";
import {useTranslations} from "../../lang";
import {useAuth} from "../../auth";
import TextArea from "antd/lib/input/TextArea";
import {DATE_FORMAT} from '../../constants'
import {CropIcon} from "../../Icons";
import {CheckCircleOutlined, PlusOutlined, ShoppingCartOutlined, SoundFilled} from "@ant-design/icons";
import OfferForm from "../OfferForm";
import OogstDatePicker from "../DatePicker";
import IconPickerModal from "../IconPickerModal";
import TaskModal from "../TaskModal";

export function loadPlanting({apiClient, gardenId, bedId, plantingId, onMissing, missingMessage}) {
    return apiClient.planting(gardenId, bedId, plantingId).catch((error) => {
        if (error.response?.status !== 404) {
            return Promise.reject(error);
        }

        message.warning(missingMessage);
        onMissing();
        return null;
    });
}

export default function Planting({gardenId, plantingId, bedId, gardens: propGardens, onCloseDrawer}) {
    const {translate} = useTranslations()
    const {plan} = useAuth()
    const navigate = useNavigate()
    const onCloseDrawerRef = useRef(onCloseDrawer)
    const translateRef = useRef(translate)
    onCloseDrawerRef.current = onCloseDrawer
    translateRef.current = translate

    const {garden_id = gardenId, bed_id = bedId, planting_id = plantingId} = useParams();
    const [garden, setGarden] = useState(null);
    const [bed, setBed] = useState(null);
    const [planting, setPlanting] = useState(null);
    const [gardens, setGardens] = useState(propGardens || []);

    const [selectedCrop, setSelectedCrop] = useState(null);
    const [form] = Form.useForm();
    const [amount, setAmount] = useState(0);
    const [offers, setOffers] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [iconPickerOpen, setIconPickerOpen] = useState(false);
    const [taskModalOpen, setTaskModalOpen] = useState(false);
    const [editingTask, setEditingTask] = useState(null);


    useEffect(() => {
        // Fetch gardens if not provided via props
        if (!propGardens || propGardens.length === 0) {
            api.gardens().then(setGardens);
        }

        if (garden_id) {
            api.garden(garden_id).then(setGarden);
        }
        if (bed_id && garden_id) {
            api.bed(garden_id, bed_id).then(setBed);
            loadPlanting({
                apiClient: api,
                gardenId: garden_id,
                bedId: bed_id,
                plantingId: planting_id,
                onMissing: () => onCloseDrawerRef.current(),
                missingMessage: translateRef.current('Deze aanplant bestaat niet meer. Het overzicht is vernieuwd.'),
            }).then((p => {
                if (!p) return;
                setPlanting(p);
                if (p.desired_amount) {
                    setAmount(p.desired_amount)
                } else if (p.plants_per_row && p.rows) {
                    setAmount(p.plants_per_row * p.rows)
                }
            }));
        } else {
            // For bedless plantings, fetch via cropplan endpoint
            api.getCropPlan().then((plantings) => {
                const p = plantings.find(pl => pl.id === parseInt(planting_id));
                if (p) {
                    setPlanting(p);
                    if (p.desired_amount) {
                        setAmount(p.desired_amount)
                    } else if (p.plants_per_row && p.rows) {
                        setAmount(p.plants_per_row * p.rows)
                    }
                    if (p.bed) {
                        setBed(p.bed);
                        if (p.bed.garden_id) {
                            api.garden(p.bed.garden_id).then(setGarden);
                        }
                    }
                    if (p.garden_id && !p.bed) {
                        api.garden(p.garden_id).then(setGarden);
                    }
                }
            });
        }
        api.getOffersByPlanting(planting_id).then(setOffers)
        api.getTasks({planting_id: planting_id}).then(setTasks)
    }, [garden_id, bed_id, planting_id, propGardens]);

    function deletePlanting() {
        Modal.confirm({
            title: translate('Weet je het zeker?'),
            content: translate('Weet je zeker dat je deze aanplant wilt verwijderen? Dit kan niet ongedaan worden gemaakt.'),
            onOk: () => {
                api.removePlanting(planting.id).then((updatedPlanting) => {
                    onCloseDrawer(updatedPlanting);
                });
            },
            okText: translate('Ja, verwijderen'),
            cancelText: translate('Annuleren')
        });


    }

    function handleIconSelect(icon) {
        setIconPickerOpen(false);
        api.setCropIcon(selectedCrop.id, icon.id).then(() => {
            setSelectedCrop({...selectedCrop, icon});
            message.success(translate('Icoon opgeslagen'));
        });
    }

    function clickCalculateDatesFromPlanting() {
        const cropDates = calculateCropDatesFromPlanting(selectedCrop, form.getFieldValue('transplant_at'))
        form.setFieldsValue({
            sow_at: cropDates.sow_at,
            prick_at: cropDates.prick_at,
            transplant_at: cropDates.transplant_at,
            harvest_at: cropDates.harvest_at,
            harvest_until: cropDates.harvest_until
        })
    }


    function clickCalculateDatesFromSowing() {
        const cropDates = calculateCropDates(selectedCrop, form.getFieldValue('sow_at'))
        form.setFieldsValue({
            sow_at: cropDates.sow_at,
            prick_at: cropDates.prick_at,
            transplant_at: cropDates.transplant_at,
            harvest_at: cropDates.harvest_at,
            harvest_until: cropDates.harvest_until
        })
    }

    function clickCalculateDatesFromHarvest() {
        const cropDates = calculateCropDatesFromHarvest(selectedCrop, form.getFieldValue('harvest_at'))
        form.setFieldsValue({
            sow_at: cropDates.sow_at,
            prick_at: cropDates.prick_at,
            transplant_at: cropDates.transplant_at,
            harvest_at: cropDates.harvest_at,
            harvest_until: cropDates.harvest_until
        })
    }

    function calculateAmounts() {
        if (!selectedCrop || !bed) {
            message.warning(translate('Selecteer eerst een bed om de aantallen te berekenen'))
            return
        }

        const {rows, plants_per_row} = calculateRowsAndPlants(bed, selectedCrop)
        setAmount(rows * plants_per_row)
        setPlanting({...planting, rows, plants_per_row: plants_per_row})

    }

    function onAmountChange() {
        const rows = form.getFieldValue('rows');
        const plants_per_row = form.getFieldValue('plants_per_row');
        setPlanting({...planting, rows, plants_per_row})
        setAmount(rows * plants_per_row)
    }

    useEffect(() => {
        if (!planting || !form) {
            return
        }

        setSelectedCrop(planting.crop)
        // Set garden_id - use bed's garden_id if available, otherwise planting's garden_id
        const gardenIdValue = planting.bed?.garden_id || planting.garden_id || garden?.id
        form.setFieldValue('garden_id', gardenIdValue)
        form.setFieldValue('bed_id', planting.bed_id)
        form.setFieldValue('status', planting.status)

        if (planting.sow_at) {
            form.setFieldValue('sow_at', dayjs(planting.sow_at))
        }
        if (planting.prick_at) {
            form.setFieldValue('prick_at', dayjs(planting.prick_at))
        }
        if (planting.transplant_at) {
            form.setFieldValue('transplant_at', dayjs(planting.transplant_at))
        }
        if (planting.harvest_at) {
            form.setFieldValue('harvest_at', dayjs(planting.harvest_at))
        }

        if (planting.harvest_until) {
            form.setFieldValue('harvest_until', dayjs(planting.harvest_until))
        }


        form.setFieldValue('crop_id', planting.crop_id)
        form.setFieldValue('rows', planting.rows)
        form.setFieldValue('plants_per_row', planting.plants_per_row)
        form.setFieldValue('desired_amount', planting.desired_amount)

        form.setFieldValue('note', planting.note)

    }, [planting, form, garden]);


    const handleGardenChange = (id) => {
        if (!id) {
            setGarden(null);
            setBed(null);
            form.setFieldsValue({garden_id: null, bed_id: null});
            setPlanting({...planting, garden_id: null, bed_id: null});
            return;
        }
        // Fetch the full garden details (with beds) from the API
        api.garden(id).then((fullGarden) => {
            setGarden(fullGarden);
            setBed(null);
            form.setFieldsValue({garden_id: id, bed_id: null});
            setPlanting({...planting, garden_id: id, bed_id: null});
        });
    }

    const handleBedChange = (id) => {
        if (!id) {
            setBed(null);
            form.setFieldsValue({bed_id: null});
            setPlanting({...planting, bed_id: null});
            return;
        }
        const value = garden.beds.find(bed => bed.id === id);
        if (!value) {
            return;
        }
        setBed(value);

        // Calculate new rows and plants per row for the new bed
        if (selectedCrop) {
            const calculated = calculateRowsAndPlants(value, selectedCrop);
            form.setFieldsValue({
                bed_id: value.id,
                rows: calculated.rows,
                plants_per_row: calculated.plants_per_row
            });
            setAmount(calculated.rows * calculated.plants_per_row);
            setPlanting({...planting, bed_id: value.id, rows: calculated.rows, plants_per_row: calculated.plants_per_row});
        } else {
            form.setFieldsValue({bed_id: value.id});
            setPlanting({...planting, bed_id: value.id});
        }
    }

    const handleDesiredAmountChange = (value) => {
        setAmount(value || 0);
        form.setFieldsValue({desired_amount: value});
        setPlanting({...planting, desired_amount: value});
    }

    function save(values) {

        const plantingData = {
            bed_id: values.bed_id || null,
            garden_id: values.garden_id || garden?.id || null,
            crop_id: values.crop_id,
            status: values.status,
            rows: values.rows,
            plants_per_row: values.plants_per_row,
            desired_amount: values.desired_amount,
            sow_at: safeFormatDate(values.sow_at),
            prick_at: safeFormatDate(values.prick_at),
            transplant_at: safeFormatDate(values.transplant_at) || safeFormatDate(values.sow_at),
            harvest_at: safeFormatDate(values.harvest_at),
            harvest_until: safeFormatDate(values.harvest_until),
            note: values.note,
        }

        if (planting) {
            api.updatePlanting(planting.id, plantingData).then((updatedPlanting) => {
                message.success(translate('Aanplant opgeslagen'));
                onCloseDrawer();
            }).catch(() => {
                Modal.error({
                    title: translate('Er is iets misgegaan'),
                    content: translate('We konden de aanplant niet opslaan. Er is iets niet correct ingevuld. Controleer of alle velden zijn ingevuld.')
                })
            });
            return;
        }

        api.addPlanting(plantingData).then(() => {
            form.resetFields();
            setSelectedCrop(null);
        }).catch((error) => {
            if (error.response && error.response.status === 422 && selectedCrop) {
                Modal.error({
                    title: translate('Er is iets misgegaan'),
                    content: (
                        <div>
                            <p>{translate('Voor dit gewas is de informatie incompleet, vul eerst alle gegevens in van het gewas. Als dit gedaan is kunnen we hem in de planner zetten.')}</p>
                            <Button onClick={() => navigate(selectedCrop.is_fully_configured ? `/crops/${selectedCrop.id}` : `/crops?cropId=${selectedCrop.id}`)}>
                                {translate('Gewas bewerken')}
                            </Button>
                        </div>
                    )
                })
            } else {
                Modal.error({
                    title: translate('Er is iets misgegaan'),
                    content: translate('We konden de aanplant niet opslaan. Er is iets niet correct ingevuld. Controleer of alle velden zijn ingevuld.')
                })
            }
        });
    }

    if (!planting || !selectedCrop) {
        return (<Spin></Spin>)
    }

    const filterOption = (input, option) =>
        (option?.label ?? '').toLowerCase().includes(input.toLowerCase());


    const gardenSelectOptions = gardens?.map((g) => {
        return {
            value: g.id,
            label: g.name,
        };
    }) || [];

    const bedSelectOptions = garden?.beds?.map((bed) => {
        return {
            value: bed.id,
            label: bed.name,
        };
    }) || [];

    const inititalValues = {
        start_at: planting.harvest_at ? dayjs(planting.harvest_at) : dayjs(),
        end_at: planting.harvest_until ? dayjs(planting.harvest_until) : dayjs().add(1, 'month'),
        stock: amount,
        image: planting.crop.image,
        title: `${planting.crop.name} - ${planting.crop.variety}`,
    }

    // Build tabs array conditionally based on plan
    const tabItems = [
                {
                    key: '1', label: (
                        <>
                            <CropIcon/> {translate('Gewas')}
                        </>
                    ), forceRender: true, children: (
                        <>
                            <Form
                                layout="horizontal"
                                labelCol={{xs: {span: 12}, md: {span: 12}}}
                                wrapperCol={{xs: {span: 12}, md: {span: 24}}}
                                form={form}
                                onFinish={save}
                            >
                                <Row gutter={18}>
                                    <Col span={12}>
                                        <Form.Item>
                                            <Button danger onClick={() => deletePlanting()}>
                                                {translate('Verwijderen')}
                                            </Button>
                                        </Form.Item>
                                    </Col>
                                    <Col span={12} style={{display: 'flex', justifyContent: 'flex-end'}}>
                                        <Form.Item>
                                            <Button type="primary" htmlType="submit">
                                                {translate('Opslaan')}
                                            </Button>
                                        </Form.Item>
                                    </Col>
                                </Row>


                                <CropInfo crop={selectedCrop}/>
                                <h1>{translate('Status en plek')}</h1>

                                <Row gutter={18}>
                                    <Col md={12} xs={24}>
                                        <Form.Item name="garden_id" label={translate('Tuin')} labelAlign="left">
                                            <Select onChange={handleGardenChange}
                                                    showSearch
                                                    allowClear
                                                    placeholder={translate('Selecteer een tuin')}
                                                    optionFilterProp="children"
                                                    filterOption={filterOption}
                                                    options={gardenSelectOptions}
                                                    value={garden?.id}
                                            >
                                            </Select>
                                        </Form.Item>
                                        <Form.Item name="bed_id" label={translate('Bed')} labelAlign="left">
                                            <Select onChange={handleBedChange}
                                                    showSearch
                                                    allowClear
                                                    placeholder={translate('Selecteer een bed')}
                                                    optionFilterProp="children"
                                                    filterOption={filterOption}
                                                    options={bedSelectOptions}
                                                    disabled={!garden}
                                            >
                                            </Select>
                                        </Form.Item>
                                        <Form.Item name="status" label="Status" labelAlign="left">
                                            <Select>

                                                {Object.keys(PLANTING_STATUS_LABELS).filter((s) => {
                                                    if (planting.crop.is_direct_sown && s === PLANTING_STATUS.TRANSPLANTED) {
                                                        return false
                                                    }
                                                    return true
                                                }).map((s) => (
                                                    <Select.Option key={s}
                                                                   value={s}>{translate(PLANTING_STATUS_LABELS[s])}</Select.Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <h1>Timing</h1>

                                {!planting.crop.is_seedling && (
                                    <Row gutter={18}>
                                        <Col md={12} xs={24}>
                                            <Form.Item name="sow_at" label={translate('Zaaien op')} labelAlign="left">
                                                <OogstDatePicker
                                                    format={DATE_FORMAT}
                                                    inputMode={"date"}
                                                    placeholder={translate('Selecteer een datum wanneer je het gaat zaaien')}
                                                    style={{width: "100%"}}
                                                />
                                            </Form.Item>

                                        </Col>
                                        <Col md={12}>
                                            <Form.Item>
                                                <Tooltip
                                                    title={translate('Als je geen zaaidatum hebt ingevuld, wordt de eerstmogelijke datum gebruikt.')}>
                                                    <Button
                                                        onClick={clickCalculateDatesFromSowing}>{translate('Bereken vanaf zaaidatum')}</Button>
                                                </Tooltip>
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                )}

                                {planting.crop.is_pricked_out && (
                                    <Row gutter={18}>
                                        <Col md={12} xs={24}>
                                            <Form.Item name="prick_at" label={translate('Verspenen op')}
                                                       labelAlign="left">
                                                <OogstDatePicker
                                                    placeholder={translate("Selecteer een datum voor verspenen")}
                                                    inputMode={"date"}
                                                />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                )}
                                {!planting.crop.is_direct_sown && (
                                    <Row gutter={18}>
                                        <Col md={12} xs={24}>
                                            <Form.Item name="transplant_at" label={translate('Planten op')}
                                                       labelAlign="left">
                                                <OogstDatePicker placeholder={translate("Selecteer een plantdatum")}/>
                                            </Form.Item>
                                        </Col>
                                        <Col md={12} xs={24}>
                                            <Tooltip
                                                title={translate('Als je geen plantdatum hebt ingevuld, wordt de eerstmogelijke datum gebruikt.')}>
                                                <Button
                                                    onClick={clickCalculateDatesFromPlanting}>{translate('Bereken vanaf plantdatum')}</Button>
                                            </Tooltip>
                                        </Col>
                                    </Row>
                                )}

                                <Row gutter={18}>
                                    <Col md={12} xs={24}>
                                        <Form.Item name="harvest_at" label={translate('Oogsten vanaf')}
                                                   labelAlign="left">
                                            <OogstDatePicker
                                                format={DATE_FORMAT}
                                                placeholder={translate('Selecteer een datum vanaf wanneer je kunt oogsten')}
                                                style={{width: "100%"}}
                                                inputMode={"date"}
                                            />
                                        </Form.Item>

                                    </Col>
                                    <Col md={12}>
                                        <Form.Item>
                                            <Tooltip
                                                title={translate('Als je geen oogstdatum hebt ingevuld, wordt de eerstmogelijke datum gebruikt.')}>
                                                <Button
                                                    onClick={clickCalculateDatesFromHarvest}>{translate('Bereken vanaf oogstdatum')}</Button>
                                            </Tooltip>
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Row gutter={12}>
                                    <Col md={12} xs={24}>
                                        <Form.Item name="harvest_until" label={translate('Oogsten tot')}
                                                   labelAlign="left">
                                            <DatePicker
                                                format={DATE_FORMAT}
                                                placeholder={translate('Selecteer een datum tot wanneer je kunt oogsten')}
                                                style={{width: "100%"}}
                                                inputMode={"date"}
                                            />
                                        </Form.Item>
                                    </Col>

                                </Row>


{/*                                <h1>{translate('Aantal')}</h1>
                                <Row gutter={18}>
                                    <Col md={12} xs={24}>
                                        <Form.Item name="desired_amount" label={translate('Gewenst aantal')}
                                                   labelAlign="left">
                                            <InputNumber
                                                         onChange={handleDesiredAmountChange}
                                                         placeholder={translate('Gewenst aantal planten')}
                                                         style={{width: "100%"}}
                                                         inputMode={"numeric"}
                                                         min={0}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col md={12} xs={24}>
                                        <div style={{paddingTop: 4, color: '#666'}}>
                                            {translate('Totaal')}: <strong>{amount}</strong> {translate('planten')}
                                        </div>
                                    </Col>
                                </Row>*/}

                                {bed && (
                                    <>
                                        <h2>{translate('Aantal rijen en planten')}</h2>
                                        <Row gutter={18}>
                                            <Col md={12} xs={24}>

                                                <Form.Item name="rows" label={translate('Aantal rijen in bed')}
                                                           labelAlign="left">
                                                    <InputNumber onChange={onAmountChange}
                                                                 placeholder={translate('Aantal rijen in bed')}
                                                                 style={{width: "100%"}}
                                                                 inputMode={"numeric"}
                                                    />
                                                </Form.Item>

                                                <Form.Item name="plants_per_row"
                                                           label={translate('Aantal planten per rij')}
                                                           labelAlign="left">
                                                    <InputNumber onChange={onAmountChange}
                                                                 placeholder={translate('Aantal planten per rij')}
                                                                 style={{width: "100%"}}
                                                                 inputMode={"numeric"}
                                                    />
                                                </Form.Item>
                                            </Col>
                                            <Col md={12} xs={24}>
                                                <Button type={"default"} onClick={calculateAmounts}>
                                                    {translate('Bereken rijen en planten per rij')}
                                                </Button>
                                            </Col>
                                            <Col md={12} xs={24}>

                                                <div id={"planting-preview"}>
                                                    <div style={{
                                                        border: '1px solid #d9d9d9',
                                                        borderRadius: 6,
                                                        padding: 4,
                                                        display: 'inline-block',
                                                        background: 'linear-gradient(145deg, #f5f3f2 0%, #f3f0ef 100%)'
                                                    }}>
                                                        <PlantingPreview
                                                            bedWidth={bed.length} bedLength={bed.width}
                                                            numRows={planting.rows}
                                                            plantsPerRow={planting.plants_per_row}
                                                            iconUrl={selectedCrop?.icon?.url}/>
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col md={12} xs={24}>
                                                <Button
                                                    onClick={() => setIconPickerOpen(true)}
                                                    title={translate('Icoon wijzigen')}
                                                >
                                                    {translate('Icoon wijzigen')}
                                                </Button>
                                            </Col>
                                        </Row>
                                    </>
                                )}

                                <h1>{translate('Notitie')}</h1>
                                <Form.Item name={"note"}>
                                    <TextArea placeholder={translate('Voeg een notitie toe')} rows={4}/>
                                </Form.Item>

                            </Form>
                        </>


                    )
                },
        {
            key: '2', label: (
                <>
                    <SoundFilled/> Updates
                </>
            ), forceRender: true, children: (
                <Row>
                    <Col md={24}>
                        <PlantingUpdates planting={planting}></PlantingUpdates>
                    </Col>
                </Row>
            )
        },
        {
            key: 'tasks', label: (
                <>
                    <CheckCircleOutlined/> {translate('Taken')}
                </>
            ), forceRender: true, children: (
                <Row>
                    <Col md={24}>
                        <Button
                            type="primary"
                            icon={<PlusOutlined/>}
                            onClick={() => { setEditingTask(null); setTaskModalOpen(true); }}
                            style={{marginBottom: 16}}
                        >
                            {translate('Taak toevoegen')}
                        </Button>
                        <Table
                            dataSource={tasks}
                            rowKey="id"
                            pagination={false}
                            columns={[
                                {
                                    title: translate('Taak'),
                                    key: 'task',
                                    render: (_, task) => (
                                        <span>
                                            <Checkbox
                                                checked={task.is_completed}
                                                onChange={(e) => {
                                                    api.toggleTask(task.id, e.target.checked).then(() => {
                                                        api.getTasks({planting_id: planting.id}).then(setTasks)
                                                    })
                                                }}
                                            />
                                            <a onClick={() => { setEditingTask(task); setTaskModalOpen(true); }}
                                               style={{marginLeft: 8}}>
                                                {task.title}
                                            </a>
                                        </span>
                                    )
                                },
                                {
                                    title: translate('Wanneer'),
                                            dataIndex: 'due_at',
                                            key: 'due_at',
                                            render: (due_at) => {
                                                const isOverdue = dayjs(due_at).isBefore(dayjs(), 'day');
                                                return <span style={{color: isOverdue ? 'red' : 'inherit'}}>{dayjs(due_at).format(DATE_FORMAT)}</span>
                                            }
                                        }
                                    ]}
                                />
                            </Col>
                        </Row>
                    )
                }
            ]

    // Add Aanbod tab only if user has selling permission
    if (plan && plan.has_selling) {
        tabItems.push({
            key: '3', label: (
                <>
                    <ShoppingCartOutlined/> Aanbod
                </>
            ), forceRender: true, children: (
                <Row>
                    <Col md={24}>
                        <h1>{translate('Verkoop')}</h1>
                        <Table dataSource={offers} columns={[
                            {key: 'title', title: 'Titel', dataIndex: 'title'},
                            {key: 'start_at', title: 'Start op', dataIndex: 'start_at', render(end_at) {
                                return dayjs(end_at).format(DATE_FORMAT)
                            }},
                            {key: 'end_at', title: 'Stopt op', dataIndex: 'end_at', render(end_at) {
                                return dayjs(end_at).format(DATE_FORMAT)
                            }},
                            {key: 'description', title: 'Omschrijving', dataIndex: 'description'},
                            {key: 'stock', title: 'Voorraad', dataIndex: 'stock'},
                            {key: 'price', title: 'Prijs', dataIndex: 'price', render(price) {
                                return printPrice(price);
                            }}
                        ]} pagination={false}/>

                        <h1>Toevoegen aan aanbod</h1>
                        <OfferForm plantingId={planting.id} initialValues={inititalValues} onOffersChanged={() => {
                            api.getOffersByPlanting(planting.id).then(setOffers)
                        }}/>
                    </Col>
                </Row>
            )
        })
    }

    return (
        <div>
            <h1>{planting.desired_amount || (planting.rows * planting.plants_per_row)}x {planting.crop.name}{planting.crop.variety ? ` - ${planting.crop.variety}` : ''}</h1>

            <Tabs defaultActiveKey="1" items={tabItems}>
            </Tabs>

            <IconPickerModal
                open={iconPickerOpen}
                onClose={() => setIconPickerOpen(false)}
                onSelect={handleIconSelect}
            />

            <TaskModal
                open={taskModalOpen}
                onCancel={() => { setTaskModalOpen(false); setEditingTask(null); }}
                onSuccess={() => api.getTasks({planting_id: planting.id}).then(setTasks)}
                task={editingTask}
                plantingId={planting.id}
                gardenId={garden?.id || planting.garden_id}
                bedId={bed?.id || planting.bed_id}
            />
        </div>
    )
}